import type { Contact, ContactListSort } from "@/types/contact.ts";

/**
 * The category selected on the contacts list.
 *
 * - `all` (default) — no category filtering
 * - `addressable` — contacts with an email or phone, i.e. someone you could
 *   actually send something to
 * - `mine` — the account's own user + assistant records
 * - `withBio` — contacts that have a researched profile
 */
export type ContactListCategory = "addressable" | "mine" | "withBio" | "all";

export const DEFAULT_CONTACT_LIST_CATEGORY: ContactListCategory = "all";

export const isContactListCategory = (value: string | null): value is ContactListCategory =>
  value === "addressable" || value === "mine" || value === "withBio" || value === "all";

/**
 * Whether an already-loaded contact row belongs in the selected category.
 *
 * `withBio` and `all` match everything: "All" filters nothing, and "With Bio" is
 * served by the list query's `hasProfile` param — whether a contact has a bio
 * isn't derivable from a list row, only from the profile the server joins in.
 */
export const matchesCategory = (contact: Contact, category: ContactListCategory): boolean => {
  const isMine = contact.type === "user" || contact.type === "assistant";
  switch (category) {
    case "mine":
      return isMine;
    case "addressable":
      return (
        !isMine &&
        (contact.handles ?? []).some((h) => h.type === "emailAddress" || h.type === "phoneNumber")
      );
    case "withBio":
    case "all":
      return true;
  }
};

/** Sort options, in menu order. Labels double as the trigger's current-value text. */
export const SORT_OPTIONS: { value: ContactListSort; label: string }[] = [
  { value: "frequency", label: "Frequency" },
  { value: "alphabetical", label: "Alphabetical" },
];

export const isContactListSort = (value: string | null): value is ContactListSort =>
  value === "frequency" || value === "alphabetical";

/**
 * Category options, in menu order. "All" leads as the default and widest view;
 * the rest narrow from there. Labels double as the trigger's current-value text.
 */
export const CATEGORY_OPTIONS: { value: ContactListCategory; label: string }[] = [
  { value: "all", label: "All" },
  { value: "mine", label: "Me" },
  { value: "withBio", label: "With Bio" },
  { value: "addressable", label: "Addressable" },
];
